import { scaleSequential } from 'd3-scale';
import { interpolateRgbBasis } from 'd3-interpolate';

import data from './data/data.json';
import {
  initAnnotationExpand,
  renderAnnotationOverlays,
} from './utils/annotations';

import './styles/graphic.scss';

// Same 7-stop ramp from the Altair notebook.
const YELLOW_ORANGE = ['#ffffd4', '#fee391', '#fec44f', '#fe9929', '#ec7014', '#cc4c02', '#8c2d04'];
// Very light gray for days with zero incidents (lighter than the notebook's #eeeeee).
const NO_DATA_COLOR = '#f2f2f2';
// True AP-style month abbreviations (the data's own `monthname` field is a
// plain `%b.` format -- Jun./Jul./Sep. -- which isn't AP style).
const AP_MONTHS = {
  1: 'Jan.',
  2: 'Feb.',
  3: 'Mar.',
  4: 'Apr.',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'Aug.',
  9: 'Sept.',
  10: 'Oct.',
  11: 'Nov.',
  12: 'Dec.',
};

const DAY_COLUMNS = 31;
const MONTH_ROWS = 12;
const MIN_CELL_WIDTH = 5;
// Mobile transposes the grid (12 month columns, 31 day rows). Rows are a fixed
// height roughly matching desktop cells; columns are capped at a slightly
// wider width, shrinking only if 12 columns wouldn't otherwise fit the screen.
const MOBILE_CELL_HEIGHT = 20;
const MOBILE_MAX_CELL_WIDTH = 24;
// Space below the panel's header, before its grid starts. Shared by the
// axis spacer calc and the header's own inline style, so the two trees can
// never drift out of sync.
const HEADER_MARGIN_BOTTOM = 4;
// Flip to true to bring back the hand-picked "Read more" callouts
// (see utils/annotations.js).
const SHOW_ANNOTATIONS = false;
// Edge-aware tooltip: gap between tile and tooltip, and the minimum margin
// kept between the tooltip and the graphic's edges.
const TOOLTIP_GAP = 8;
const TOOLTIP_EDGE_INSET = 4;
// Width of the rotated axis title ("Month" / "Day of month") left of the axis.
const AXIS_TITLE_WIDTH = 16;
// On mobile only these month columns get a tick label -- all 12 won't fit at
// a readable size.
const MOBILE_MONTH_TICKS = [1, 4, 7, 9, 12];
const LEGEND_GRADIENT_STOPS = 12;

export default function (props, container, isMobile) {
  const { initialWidth } = props;

  container.html('');

  // ====================================
  //  data.json is already one pre-aggregated row per month/day
  //  (count = total across all years), so just index it by month/day.
  // ====================================
  const byMonthDay = new Map(data.map(row => [
    `${Number(row.month_no)}-${Number(row.day)}`,
    { total: Number(row.count) || 0, isFootball: row.football_homecoming === true },
  ]));

  const maxCount = Array.from(byMonthDay.values())
    .reduce((max, entry) => Math.max(max, entry.total), 0);
  const colorScale = scaleSequential(interpolateRgbBasis(YELLOW_ORANGE))
    .domain([1, Math.max(maxCount, 2)]);

  // ====================================
  //  Sizing. Desktop: months down the side, days across (31 columns) with
  //  square cells from the available width. Mobile: transposed -- months
  //  across (12 columns), days down (31 rows), with rows of MOBILE_CELL_HEIGHT and
  //  columns capped at MOBILE_MAX_CELL_WIDTH.
  // ====================================
  const monthAxisWidth = isMobile ? 20 : 34;
  const panelPadding = isMobile ? 4 : 6;
  const gridGap = isMobile ? 1 : 2;
  const headerHeight = isMobile ? 34 : 42;

  // Grid dimensions: columns/rows differ by orientation.
  const gridColumns = isMobile ? MONTH_ROWS : DAY_COLUMNS;
  const gridRows = isMobile ? DAY_COLUMNS : MONTH_ROWS;

  const panelWidth = Math.max(240, initialWidth - monthAxisWidth - AXIS_TITLE_WIDTH);
  const availableGridWidth = panelWidth - (panelPadding * 2) - (gridGap * (gridColumns - 1));
  const fitCellWidth = Math.max(MIN_CELL_WIDTH, Math.floor(availableGridWidth / gridColumns));
  const cellWidth = isMobile ? Math.min(fitCellWidth, MOBILE_MAX_CELL_WIDTH) : fitCellWidth;
  const cellHeight = isMobile ? MOBILE_CELL_HEIGHT : cellWidth;
  const gridHeight = (cellHeight * gridRows) + (gridGap * (gridRows - 1));

  // ====================================
  //  DOM scaffold
  // ====================================
  const root = container.append('div')
    .attr('class', `calendar-graphic${isMobile ? ' calendar-graphic--mobile' : ''}`);

  buildLegend(root, colorScale, maxCount);

  const monthAxisSpacerHeight = panelPadding + headerHeight + HEADER_MARGIN_BOTTOM;

  const body = root.append('div').attr('class', 'calendar-body');
  buildLeftAxisTitle(body, {
    text: isMobile ? 'Day of month' : 'Month',
    spacerHeight: monthAxisSpacerHeight,
    gridHeight,
  });

  const buildAxis = isMobile ? buildDayAxis : buildMonthAxis;
  buildAxis(body, {
    axisWidth: monthAxisWidth,
    spacerHeight: monthAxisSpacerHeight,
    gridHeight,
    cellHeight,
    gridGap,
  });

  buildPanel(body, byMonthDay, {
    colorScale,
    panelPadding,
    headerHeight,
    cellWidth,
    cellHeight,
    gridGap,
    isMobile,
  });

  if (SHOW_ANNOTATIONS) initAnnotationExpand(root.node());
  initCellTooltips(root);
}

// ====================================
//  Legend
// ====================================
function buildLegend(root, colorScale, maxCount) {
  const legend = root.append('div').attr('class', 'calendar-legend');

  // Continuous gradient ruler from 1 incident to the max, sampled from the
  // same scale the cells use.
  const counts = legend.append('div').attr('class', 'calendar-legend__counts');
  counts.append('span')
    .attr('class', 'calendar-legend__label calendar-legend__counts-lead')
    .text('Incidents on each date, 2008 through 2026*:');

  const maxValue = Math.max(maxCount, 2);
  const stops = [];
  for (let i = 0; i <= LEGEND_GRADIENT_STOPS; i += 1) {
    const fraction = i / LEGEND_GRADIENT_STOPS;
    stops.push(`${colorScale(1 + ((maxValue - 1) * fraction))} ${(fraction * 100).toFixed(1)}%`);
  }

  const ruler = counts.append('div').attr('class', 'calendar-legend__ruler');
  ruler.append('div')
    .attr('class', 'calendar-legend__ruler-bar')
    .style('background', `linear-gradient(to right, ${stops.join(', ')})`);

  // Labels only (no tick marks): 1, the round midpoint (5 when max is 10), and the max.
  const tickValues = Array.from(new Set([1, Math.round(maxValue / 2), maxValue]));
  tickValues.forEach((value) => {
    const left = ((value - 1) / (maxValue - 1)) * 100;
    ruler.append('span')
      .attr('class', 'calendar-legend__ruler-label')
      .style('left', `${left}%`)
      .text(String(value));
  });

  const noData = legend.append('div').attr('class', 'calendar-legend__item');
  noData.append('span').attr('class', 'calendar-legend__swatch calendar-legend__swatch--empty');
  noData.append('span').attr('class', 'calendar-legend__label').text('No incidents');

  const football = legend.append('div').attr('class', 'calendar-legend__item');
  football.append('span').attr('class', 'calendar-legend__swatch calendar-legend__swatch--outline');
  football.append('span').attr('class', 'calendar-legend__label').text('At least one football or homecoming-related shooting');
}

// Rotated title for the left axis, spanning the grid's height.
function buildLeftAxisTitle(body, { text, spacerHeight, gridHeight }) {
  const wrap = body.append('div')
    .attr('class', 'axis-title-left')
    .style('width', `${AXIS_TITLE_WIDTH}px`);

  wrap.append('div')
    .attr('class', 'axis-title-left__spacer')
    .style('height', `${spacerHeight}px`);

  wrap.append('div')
    .attr('class', 'axis-title-left__text')
    .style('height', `${gridHeight}px`)
    .text(text);
}

// ====================================
//  Desktop left axis: months (row heights match grid row heights)
// ====================================
function buildMonthAxis(body, {
  axisWidth, spacerHeight, gridHeight, cellHeight, gridGap,
}) {
  const monthAxis = body.append('div')
    .attr('class', 'month-axis')
    .style('width', `${axisWidth}px`);

  monthAxis.append('div')
    .attr('class', 'month-axis__spacer')
    .style('height', `${spacerHeight}px`);

  const rows = monthAxis.append('div')
    .attr('class', 'month-axis__rows')
    .style('height', `${gridHeight}px`);

  for (let month = 1; month <= MONTH_ROWS; month += 1) {
    rows.append('div')
      .attr('class', 'month-axis__label')
      .style('height', `${cellHeight}px`)
      .style('margin-bottom', month < MONTH_ROWS ? `${gridGap}px` : '0')
      .text(AP_MONTHS[month]);
  }
}

// ====================================
//  Mobile left axis: days 1-31 (row heights match grid row heights)
// ====================================
function buildDayAxis(body, {
  axisWidth, spacerHeight, gridHeight, cellHeight, gridGap,
}) {
  const dayAxis = body.append('div')
    .attr('class', 'day-axis')
    .style('width', `${axisWidth}px`);

  dayAxis.append('div')
    .attr('class', 'day-axis__spacer')
    .style('height', `${spacerHeight}px`);

  const rows = dayAxis.append('div')
    .attr('class', 'day-axis__rows')
    .style('height', `${gridHeight}px`);

  for (let day = 1; day <= DAY_COLUMNS; day += 1) {
    rows.append('div')
      .attr('class', 'day-axis__label')
      .style('height', `${cellHeight}px`)
      .style('margin-bottom', day < DAY_COLUMNS ? `${gridGap}px` : '0')
      .text(String(day));
  }
}

// ====================================
//  The single panel: header + 12×31 grid, summed across all years
// ====================================
function buildPanel(body, byMonthDay, {
  colorScale, panelPadding, headerHeight, cellWidth, cellHeight, gridGap, isMobile,
}) {
  const panel = body.append('div')
    .attr('class', 'year-panel')
    .style('padding', `${panelPadding}px`);

  const header = panel.append('div')
    .attr('class', 'year-header')
    .style('height', `${headerHeight}px`)
    .style('margin-bottom', `${HEADER_MARGIN_BOTTOM}px`);

  header.append('div')
    .attr('class', 'year-header__year')
    .text(isMobile ? 'Month' : 'Day of month');

  const gridColumns = isMobile ? MONTH_ROWS : DAY_COLUMNS;
  const gridRows = isMobile ? DAY_COLUMNS : MONTH_ROWS;

  // Top ticks: month names across on mobile, day numbers across on desktop.
  const ticks = header.append('div')
    .attr('class', 'year-header__ticks')
    .style('grid-template-columns', `repeat(${gridColumns}, ${cellWidth}px)`)
    .style('gap', `0 ${gridGap}px`);

  for (let i = 1; i <= gridColumns; i += 1) {
    const showTick = !isMobile || MOBILE_MONTH_TICKS.includes(i);
    let label = '';
    if (showTick) label = isMobile ? AP_MONTHS[i] : String(i);
    ticks.append('div')
      .attr('class', 'year-header__tick')
      .text(label);
  }

  const grid = panel.append('div')
    .attr('class', 'year-grid')
    .style('grid-template-columns', `repeat(${gridColumns}, ${cellWidth}px)`)
    .style('grid-template-rows', `repeat(${gridRows}, ${cellHeight}px)`)
    .style('gap', `${gridGap}px`);

  for (let month = 1; month <= MONTH_ROWS; month += 1) {
    for (let day = 1; day <= DAY_COLUMNS; day += 1) {
      const rowData = byMonthDay.get(`${month}-${day}`);
      if (!rowData) continue;

      const { total, isFootball } = rowData;
      const fill = total > 0 ? colorScale(total) : NO_DATA_COLOR;
      const dateLabel = `${AP_MONTHS[month]} ${day}`;
      const countLabel = formatTotal(total);

      grid.append('div')
        .attr('class', `cell${isFootball ? ' cell--football' : ''}`)
        .attr('data-month', month)
        .attr('data-day', day)
        .attr('data-total', total)
        .attr('aria-label', `${dateLabel}: ${countLabel}`)
        .style('grid-row', isMobile ? day : month)
        .style('grid-column', isMobile ? month : day)
        .style('background-color', fill);
    }
  }

  if (SHOW_ANNOTATIONS) renderAnnotationOverlays(panel.node(), isMobile);
}

// ====================================
//  Tooltips
// ====================================
function formatTotal(total) {
  if (total === 0) return 'No shootings across all years';
  return `${total} ${total === 1 ? 'shooting' : 'shootings'} across all years`;
}

// Orders the placements to try, based on where the tile sits in the grid:
// left-edge tiles open to the right, right-edge tiles open to the left,
// lower tiles open above, and everything else prefers below.
function getPlacementOrder(tx, ty) {
  if (tx < 0.25) return ['right', 'above', 'below', 'left'];
  if (tx > 0.75) return ['left', 'above', 'below', 'right'];
  if (ty > 0.5) return ['above', 'below', 'right', 'left'];
  return ['below', 'above', 'right', 'left'];
}

// Positions the tooltip beside/above/below the tile, always inside the
// graphic's own bounds. All math is in the root's coordinate space (the
// tooltip is absolutely positioned inside the position:relative root).
function positionTooltip(tooltip, cell, rootNode, gridNode) {
  const rootRect = rootNode.getBoundingClientRect();
  const gridRect = gridNode.getBoundingClientRect();
  const cellRect = cell.getBoundingClientRect();

  const frameW = rootRect.width;
  const frameH = rootRect.height;
  const w = tooltip.offsetWidth;
  const h = tooltip.offsetHeight;

  const cell_ = {
    left: cellRect.left - rootRect.left,
    right: cellRect.right - rootRect.left,
    top: cellRect.top - rootRect.top,
    bottom: cellRect.bottom - rootRect.top,
  };
  const cx = (cell_.left + cell_.right) / 2;
  const cy = (cell_.top + cell_.bottom) / 2;

  const tx = ((cellRect.left + (cellRect.width / 2)) - gridRect.left) / gridRect.width;
  const ty = ((cellRect.top + (cellRect.height / 2)) - gridRect.top) / gridRect.height;

  const boxFor = {
    above: { left: cx - (w / 2), top: cell_.top - TOOLTIP_GAP - h },
    below: { left: cx - (w / 2), top: cell_.bottom + TOOLTIP_GAP },
    left: { left: cell_.left - TOOLTIP_GAP - w, top: cy - (h / 2) },
    right: { left: cell_.right + TOOLTIP_GAP, top: cy - (h / 2) },
  };

  const overflowOf = ({ left, top }) => (
    Math.max(0, TOOLTIP_EDGE_INSET - left)
    + Math.max(0, (left + w) - (frameW - TOOLTIP_EDGE_INSET))
    + Math.max(0, TOOLTIP_EDGE_INSET - top)
    + Math.max(0, (top + h) - (frameH - TOOLTIP_EDGE_INSET))
  );

  // First candidate that fits wins; otherwise the least-overflowing one.
  let best = null;
  const order = getPlacementOrder(tx, ty);
  for (let i = 0; i < order.length; i += 1) {
    const overflow = overflowOf(boxFor[order[i]]);
    if (best === null || overflow < best.overflow) best = { box: boxFor[order[i]], overflow };
    if (overflow === 0) break;
  }

  const left = Math.min(
    Math.max(TOOLTIP_EDGE_INSET, best.box.left),
    Math.max(TOOLTIP_EDGE_INSET, frameW - w - TOOLTIP_EDGE_INSET),
  );
  const top = Math.min(
    Math.max(TOOLTIP_EDGE_INSET, best.box.top),
    Math.max(TOOLTIP_EDGE_INSET, frameH - h - TOOLTIP_EDGE_INSET),
  );

  tooltip.style.left = `${left}px`;
  tooltip.style.top = `${top}px`;
}

// One tooltip per graphic. Mouse: hover. Touch: tap a tile to show it, tap
// another to move it, tap anywhere else to dismiss.
function initCellTooltips(root) {
  const rootNode = root.node();
  const gridNode = rootNode.querySelector('.year-grid');
  if (!gridNode) return;

  const tooltipSel = root.append('div')
    .attr('class', 'cell-tooltip')
    .attr('role', 'tooltip');
  const dateEl = tooltipSel.append('div').attr('class', 'cell-tooltip__date').node();
  const totalEl = tooltipSel.append('div').attr('class', 'cell-tooltip__total').node();
  const tooltip = tooltipSel.node();

  let activeCell = null;

  const hide = () => {
    tooltip.classList.remove('is-visible');
    if (activeCell) activeCell.classList.remove('is-active');
    activeCell = null;
  };

  const show = (cell) => {
    if (activeCell && activeCell !== cell) activeCell.classList.remove('is-active');
    activeCell = cell;
    cell.classList.add('is-active');

    const month = Number(cell.getAttribute('data-month'));
    const day = Number(cell.getAttribute('data-day'));
    const total = Number(cell.getAttribute('data-total'));
    dateEl.textContent = `${AP_MONTHS[month]} ${day}`;
    totalEl.textContent = formatTotal(total);

    positionTooltip(tooltip, cell, rootNode, gridNode);
    tooltip.classList.add('is-visible');
  };

  gridNode.addEventListener('pointerover', (e) => {
    if (e.pointerType !== 'mouse') return;
    const cell = e.target.closest('.cell');
    if (cell) show(cell);
  });

  gridNode.addEventListener('pointerout', (e) => {
    if (e.pointerType !== 'mouse') return;
    const next = e.relatedTarget;
    if (!(next && next.closest && next.closest('.cell'))) hide();
  });

  rootNode.addEventListener('click', (e) => {
    const cell = e.target.closest('.cell');
    if (cell) show(cell);
    else hide();
  });
}
